import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Loads all master data needed by the Material Sales view and exposes
 * lookup helpers. Keeps SalesView lean (pure-extraction refactor).
 */
export const useSalesMasterData = () => {
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [salesOrgDistChannelAssignments, setSalesOrgDistChannelAssignments] = useState([]);
  const [plants, setPlants] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [accountAssignmentGroups, setAccountAssignmentGroups] = useState([]);
  const [availabilityChecks, setAvailabilityChecks] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [transportationGroups, setTransportationGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [
          salesOrgRes, distChannelRes, assignmentsRes, plantRes, uomRes,
          aacRes, acRes, pcRes, tgRes, lgRes,
        ] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`),
          axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`),
          axios.get(`${BACKEND_URL}/api/assignments/sales-org-dist-channel`),
          axios.get(`${BACKEND_URL}/api/material-management/plants`),
          axios.get(`${BACKEND_URL}/api/units-of-measure`),
          axios.get(`${BACKEND_URL}/api/account-assignment-groups`),
          axios.get(`${BACKEND_URL}/api/availability-checks`),
          axios.get(`${BACKEND_URL}/api/controlling/profit-centers`),
          axios.get(`${BACKEND_URL}/api/transportation-groups`),
          axios.get(`${BACKEND_URL}/api/loading-groups`),
        ]);

        setSalesOrgs(salesOrgRes.data || []);
        setDistChannels(distChannelRes.data || []);
        setSalesOrgDistChannelAssignments(assignmentsRes.data || []);
        setPlants(plantRes.data || []);
        setUnitsOfMeasure(uomRes.data || []);
        setAccountAssignmentGroups(aacRes.data || []);
        setAvailabilityChecks(acRes.data || []);
        setProfitCenters(pcRes.data || []);
        setTransportationGroups(tgRes.data || []);
        setLoadingGroups(lgRes.data || []);
      } catch (error) {
        console.error('Error loading master data:', error);
        toast.error('Failed to load master data');
      } finally {
        setLoading(false);
      }
    };
    loadMasterData();
  }, []);

  const getSalesOrgName = (id) => salesOrgs.find((s) => s.sales_org_id === id)?.sales_org_name || id;
  const getDistChannelName = (id) => distChannels.find((d) => d.dist_channel_id === id)?.dist_channel_name || id;
  const getPlantName = (id) => plants.find((p) => p.plant_id === id)?.plant_name || id;

  // Distribution channels assigned to the selected sales org.
  const getAvailableDistChannels = (salesOrgId) => {
    if (!salesOrgId) return [];
    const assignedDistChannelIds = salesOrgDistChannelAssignments
      .filter((a) => a.sales_org_id === salesOrgId)
      .map((a) => a.dist_channel_id);
    return distChannels.filter((dc) => assignedDistChannelIds.includes(dc.dist_channel_id));
  };

  return {
    salesOrgs, distChannels, plants, unitsOfMeasure, accountAssignmentGroups,
    availabilityChecks, profitCenters, transportationGroups, loadingGroups, loading,
    getSalesOrgName, getDistChannelName, getPlantName, getAvailableDistChannels,
  };
};
